import { useState, useEffect, useMemo, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Plus, MoreHorizontal, DollarSign, X, ChevronDown, Upload, Check, ChevronsUpDown } from 'lucide-react';
import { ColDef, ICellRendererParams } from 'ag-grid-community';
import { parseISO, format } from 'date-fns';
import { useAuth } from '@/contexts/AuthContext';
import { useAccessControl } from '@/contexts/AccessControlContext';
import { Modules, Permissions } from '@prolarus/shared';
import { useToast } from '@/hooks/use-toast';
import { usePagination } from '@/hooks/usePagination';
import { invoiceService, invoiceItemService, statusService, externalSystemService, vendorService, profileService } from '@/services';
import { cn } from '@/lib/utils';
import NewInvoiceDialog from '@/components/invoice/NewInvoiceDialog';
import { AGDataGrid } from '@/components/ui/ag-data-grid';
import { Button } from '@/components/ui/button';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from '@/components/ui/command';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog';

interface Invoice {
  id: string;
  vendor_id: string | null;
  vendor: { name: string } | null;
  invoice_number: string | null;
  total_amount: number;
  invoice_date: string | null;
  discount_amt: number | null;
  discount_expiration: string | null;
  status_id: string | null;
  current_status: { id: string; name: string; readonly: boolean } | null;
  external_system: string | null;
  quote_id: string | null;
  compliance_checks: { has_mismatch: boolean }[];
  created: string;
}

interface ExternalSystem {
  id: string;
  name: string;
}

interface Status {
  id: string;
  name: string;
}

export default function Invoices() {
  const { user } = useAuth();
  const { canPerformAction } = useAccessControl();
  const canCreate = canPerformAction(Modules.COMPLIANCE, Permissions.invoice.create.code);
  const canUpload = canPerformAction(Modules.COMPLIANCE, Permissions.invoice.upload.code);
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();

  // Pagination hook
  const {
    pageIndex,
    pageSize,
    searchValue,
    debouncedSearch,
    setSearchValue,
    setPageIndex,
    setPageSize,
    resetToFirstPage,
    pageSizeOptions,
  } = usePagination({ defaultPageSize: 25, storageKey: 'invoices' });

  // Filter state
  const [selectedVendorId, setSelectedVendorId] = useState<string | null>(null);
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);
  const [selectedExternalSystem, setSelectedExternalSystem] = useState<string | null>(
    searchParams.get('external_system')
  );
  const [vendorPopoverOpen, setVendorPopoverOpen] = useState(false);

  // Data state
  const [invoices, setInvoices] = useState<Invoice[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const [isLoading, setIsLoading] = useState(true);
  const [companyId, setCompanyId] = useState<string | null>(null);
  const [itemCounts, setItemCounts] = useState<Record<string, number>>({});

  // Filter dropdown data
  const [vendors, setVendors] = useState<{ id: string; name: string }[]>([]);
  const [invoiceStatuses, setInvoiceStatuses] = useState<Status[]>([]);
  const [externalSystems, setExternalSystems] = useState<ExternalSystem[]>([]);

  // Dialog state
  const [newInvoiceDialogOpen, setNewInvoiceDialogOpen] = useState(false);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [invoiceToDelete, setInvoiceToDelete] = useState<Invoice | null>(null);

  // Fetch company ID and filter data on mount
  useEffect(() => {
    const fetchInitialData = async () => {
      if (!user) return;

      try {
        const profileResult = await profileService.get(user.id);
        const profile = profileResult.data;

        if (!profile?.company_id) {
          setIsLoading(false);
          return;
        }

        setCompanyId(profile.company_id);

        // Fetch filter data in parallel
        const [vendorsResult, statusesResult, externalSystemsResult] = await Promise.all([
          // Vendors filtered by active status
          (async () => {
            const vendorStatuses = await statusService.listByEntityType('vendor', profile.company_id);
            const activeStatusId = vendorStatuses.data?.find((s) =>
              s.name?.toLowerCase().includes('active'),
            )?.id ?? null;
            const result = await vendorService.listActive(profile.company_id, activeStatusId);
            return (result.data ?? []).map((v) => ({ id: v.id, name: v.name }));
          })(),
          statusService.listByEntityType('invoice', profile.company_id),
          externalSystemService.list(profile.company_id),
        ]);

        setVendors(vendorsResult);
        if (!statusesResult.error) setInvoiceStatuses(statusesResult.data);
        if (!externalSystemsResult.error) setExternalSystems(externalSystemsResult.data as ExternalSystem[]);
      } catch (error: any) {
        console.error('Error fetching initial data:', error);
        toast({
          title: 'Error',
          description: 'Failed to load data.',
          variant: 'destructive',
        });
      }
    };

    fetchInitialData();
  }, [user, toast]);

  // Fetch paginated invoices when pagination/filters change
  useEffect(() => {
    const fetchInvoices = async () => {
      if (!companyId) return;

      setIsLoading(true);
      try {
        const result = await invoiceService.listWithRelationsPaginated(
          companyId,
          { page: pageIndex, pageSize },
          {
            search: debouncedSearch,
            vendorId: selectedVendorId,
            statusId: selectedStatus,
            externalSystem: selectedExternalSystem,
          }
        );

        if (result.error) throw result.error;

        setInvoices(result.data as Invoice[]);
        setTotalCount(result.totalCount);

        // Fetch item counts for current page's invoices only
        const invoiceIds = (result.data || []).map((inv) => inv.id);
        const countsResult = await invoiceItemService.countByInvoices(invoiceIds);
        if (countsResult.error) throw countsResult.error;
        setItemCounts(countsResult.data);
      } catch (error: any) {
        console.error('Error fetching invoices:', error);
        toast({
          title: 'Error',
          description: 'Failed to load invoices.',
          variant: 'destructive',
        });
      } finally {
        setIsLoading(false);
      }
    };

    fetchInvoices();
  }, [companyId, pageIndex, pageSize, debouncedSearch, selectedVendorId, selectedStatus, selectedExternalSystem, toast]);

  // Sync external system filter with URL
  useEffect(() => {
    const currentParams = new URLSearchParams(searchParams);
    if (selectedExternalSystem) {
      currentParams.set('external_system', selectedExternalSystem);
    } else {
      currentParams.delete('external_system');
    }
    setSearchParams(currentParams, { replace: true });
  }, [selectedExternalSystem]);

  // Filter change handlers (reset to first page)
  const handleVendorChange = (vendorId: string | null) => {
    setSelectedVendorId(vendorId);
    resetToFirstPage();
  };

  const handleStatusChange = (value: string) => {
    setSelectedStatus(value === "all" ? null : value);
    resetToFirstPage();
  };

  const handleExternalSystemChange = (value: string) => {
    setSelectedExternalSystem(value === "all" ? null : value);
    resetToFirstPage();
  };

  const hasActiveFilters = searchValue || selectedVendorId || selectedStatus || selectedExternalSystem;

  const clearFilters = () => {
    setSearchValue('');
    setSelectedVendorId(null);
    setSelectedStatus(null);
    setSelectedExternalSystem(null);
    resetToFirstPage();
  };

  // Row click handler
  const handleRowClick = useCallback((invoice: Invoice) => {
    if (invoice.quote_id) {
      navigate(`/invoices/${invoice.id}/quote-basis`);
    } else {
      navigate(`/invoices/${invoice.id}`);
    }
  }, [navigate]);

  // Delete handlers
  const handleDeleteClick = (invoice: Invoice) => {
    setInvoiceToDelete(invoice);
    setDeleteDialogOpen(true);
  };

  const confirmDelete = async () => {
    if (!invoiceToDelete || !companyId || !user?.id) return;

    try {
      const result = await invoiceService.delete(
        invoiceToDelete.id,
        invoiceToDelete as unknown as Record<string, unknown>,
        { companyId, userId: user.id }
      );

      if (result.error) throw result.error;

      toast({ title: 'Deleted', description: 'Invoice deleted successfully' });

      // Refetch current page
      const refreshResult = await invoiceService.listWithRelationsPaginated(
        companyId,
        { page: pageIndex, pageSize },
        {
          search: debouncedSearch,
          vendorId: selectedVendorId,
          statusId: selectedStatus,
          externalSystem: selectedExternalSystem,
        }
      );

      if (!refreshResult.error) {
        if (refreshResult.data.length === 0 && pageIndex > 0) {
          setPageIndex(pageIndex - 1);
        } else {
          setInvoices(refreshResult.data as Invoice[]);
          setTotalCount(refreshResult.totalCount);
        }
      }
    } catch (error: any) {
      console.error('Error deleting invoice:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to delete invoice.',
        variant: 'destructive',
      });
    } finally {
      setDeleteDialogOpen(false);
      setInvoiceToDelete(null);
    }
  };

  // Refetch helper for after creating new invoice
  const refetchInvoices = async () => {
    if (!companyId) return;
    const result = await invoiceService.listWithRelationsPaginated(
      companyId,
      { page: pageIndex, pageSize },
      {
        search: debouncedSearch,
        vendorId: selectedVendorId,
        statusId: selectedStatus,
        externalSystem: selectedExternalSystem,
      }
    );
    if (!result.error) {
      setInvoices(result.data as Invoice[]);
      setTotalCount(result.totalCount);
    }
  };

  // Column definitions
  const columnDefs = useMemo<ColDef<Invoice>[]>(() => [
    {
      colId: 'compliance',
      headerName: '',
      width: 50,
      sortable: false,
      resizable: false,
      suppressSizeToFit: true,
      cellRenderer: (params: ICellRendererParams<Invoice>) => {
        if (!params.data) return null;
        const checks = params.data.compliance_checks;
        const color = !checks?.length
          ? 'bg-gray-400'
          : checks.some(c => c.has_mismatch)
            ? 'bg-red-500'
            : 'bg-green-500';
        return (
          <div className="flex items-center justify-center h-full">
            <span className={`inline-block w-2.5 h-2.5 rounded-full ${color}`} />
          </div>
        );
      },
    },
    {
      field: 'vendor',
      headerName: 'Vendor',
      width: 180,
      sortable: true,
      valueGetter: (params) => params.data?.vendor?.name || 'No vendor',
    },
    {
      field: 'invoice_number',
      headerName: 'Invoice',
      flex: 1,
      minWidth: 180,
      sortable: true,
      cellRenderer: (params: ICellRendererParams<Invoice>) => (
        <span className="font-medium">
          {params.data?.invoice_number || params.data?.id?.slice(0, 8)}
        </span>
      ),
    },
    {
      field: 'total_amount',
      headerName: 'Amount',
      width: 140,
      sortable: true,
      cellStyle: { textAlign: 'right' },
      headerClass: 'ag-right-aligned-header',
      cellRenderer: (params: ICellRendererParams<Invoice>) => (
        <div className="flex items-center justify-end gap-1 font-medium w-full">
          <DollarSign className="w-4 h-4 text-muted-foreground" />
          {(params.data?.total_amount ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
        </div>
      ),
    },
    {
      colId: 'items',
      headerName: 'Items',
      width: 100,
      sortable: false,
      valueGetter: (params) => params.data ? (itemCounts[params.data.id] || 0) : 0,
      valueFormatter: (params) => `${params.value} items`,
    },
    {
      field: 'invoice_date',
      headerName: 'Date',
      width: 130,
      sortable: true,
      valueGetter: (params) => {
        const dateStr = params.data?.invoice_date || params.data?.created;
        return dateStr || null;
      },
      valueFormatter: (params) => {
        if (!params.value) return '—';
        try {
          return format(parseISO(params.value), 'MMM d, yyyy');
        } catch {
          return '—';
        }
      },
      cellClass: 'text-muted-foreground',
    },
    {
      field: 'discount_amt',
      headerName: 'Discount Amt',
      width: 140,
      sortable: true,
      cellStyle: { textAlign: 'right' },
      headerClass: 'ag-right-aligned-header',
      cellRenderer: (params: ICellRendererParams<Invoice>) => {
        if (params.data?.discount_amt == null) return <span className="text-muted-foreground block text-center w-full">—</span>;
        return (
          <div className="flex items-center justify-end gap-1 font-medium w-full">
            <DollarSign className="w-4 h-4 text-muted-foreground" />
            {params.data.discount_amt.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </div>
        );
      },
    },
    {
      field: 'discount_expiration',
      headerName: 'Discount Expires',
      width: 140,
      sortable: true,
      valueFormatter: (params) => {
        if (!params.value) return '—';
        try {
          return format(parseISO(params.value), 'MMM d, yyyy');
        } catch {
          return '—';
        }
      },
      cellClass: 'text-muted-foreground',
    },
    {
      field: 'current_status',
      headerName: 'Status',
      width: 140,
      sortable: true,
      valueGetter: (params) => params.data?.current_status?.name,
      cellRenderer: (params: ICellRendererParams<Invoice>) =>
        params.data?.current_status ? (
          <span className="badge badge-secondary">
            {params.data.current_status.name}
          </span>
        ) : (
          <span className="text-muted-foreground">—</span>
        ),
    },
    {
      field: 'created',
      headerName: 'Created',
      width: 130,
      sortable: true,
      cellRenderer: (params: ICellRendererParams<Invoice>) => {
        if (!params.value) return <span className="text-muted-foreground">—</span>;
        const date = new Date(params.value);
        return (
          <div className="flex flex-col text-muted-foreground leading-tight">
            <span className="text-xs">{date.toLocaleDateString()}</span>
            <span className="text-[10px]">{date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</span>
          </div>
        );
      },
    },
    {
      colId: 'actions',
      headerName: '',
      width: 60,
      sortable: false,
      resizable: false,
      suppressSizeToFit: true,
      cellRenderer: (params: ICellRendererParams<Invoice>) => (
        <div onClick={(e) => e.stopPropagation()}>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="btn-ghost p-2">
                <MoreHorizontal className="w-5 h-5" />
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => params.data && navigate(
                params.data.quote_id
                  ? `/invoices/${params.data.id}/quote-basis`
                  : `/invoices/${params.data.id}`
              )}>
                View Details
              </DropdownMenuItem>
              <DropdownMenuItem
                onClick={() => params.data && navigate(`/invoices/${params.data.id}/edit`)}
                disabled={params.data?.current_status?.readonly === true}
              >
                Edit
              </DropdownMenuItem>
              <DropdownMenuItem
                className="text-destructive"
                onClick={() => params.data && handleDeleteClick(params.data)}
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      ),
    },
  ], [itemCounts, navigate]);

  // Toolbar actions
  const toolbarActions = (
    <div className="flex items-center gap-2">
      {/* Vendor Filter */}
      <Popover open={vendorPopoverOpen} onOpenChange={setVendorPopoverOpen}>
        <PopoverTrigger asChild>
          <Button
            variant="outline"
            role="combobox"
            className="w-[200px] justify-between"
          >
            <span className="truncate">
              {selectedVendorId
                ? vendors.find(v => v.id === selectedVendorId)?.name
                : "All Vendors"}
            </span>
            <ChevronsUpDown className="ml-2 h-4 w-4 shrink-0 opacity-50" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-[280px] p-0" align="start">
          <Command>
            <CommandInput placeholder="Search vendors..." />
            <CommandList>
              <CommandEmpty>No vendor found.</CommandEmpty>
              <CommandGroup>
                <CommandItem
                  value="all"
                  onSelect={() => {
                    handleVendorChange(null);
                    setVendorPopoverOpen(false);
                  }}
                >
                  <Check
                    className={cn(
                      "mr-2 h-4 w-4",
                      !selectedVendorId ? "opacity-100" : "opacity-0"
                    )}
                  />
                  All Vendors
                </CommandItem>
                {vendors.map((vendor) => (
                  <CommandItem
                    key={vendor.id}
                    value={vendor.name}
                    onSelect={() => {
                      handleVendorChange(vendor.id);
                      setVendorPopoverOpen(false);
                    }}
                  >
                    <Check
                      className={cn(
                        "mr-2 h-4 w-4",
                        selectedVendorId === vendor.id ? "opacity-100" : "opacity-0"
                      )}
                    />
                    {vendor.name}
                  </CommandItem>
                ))}
              </CommandGroup>
            </CommandList>
          </Command>
        </PopoverContent>
      </Popover>

      {/* Status Filter */}
      <Select
        value={selectedStatus || "all"}
        onValueChange={handleStatusChange}
      >
        <SelectTrigger className="w-[160px]">
          <SelectValue placeholder="All Statuses" />
        </SelectTrigger>
        <SelectContent>
          <SelectItem value="all">All Statuses</SelectItem>
          {invoiceStatuses.map((status) => (
            <SelectItem key={status.id} value={status.id}>
              {status.name}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>

      {/* External System Filter */}
      {externalSystems.length > 0 && (
        <Select
          value={selectedExternalSystem || "all"}
          onValueChange={handleExternalSystemChange}
        >
          <SelectTrigger className="w-[180px]">
            <SelectValue placeholder="All External Systems" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">All External Systems</SelectItem>
            {externalSystems.map((system) => (
              <SelectItem key={system.id} value={system.name}>
                {system.name}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      )}

      {/* Clear Filters Button */}
      {hasActiveFilters && (
        <Button
          variant="ghost"
          size="sm"
          onClick={clearFilters}
        >
          <X className="w-4 h-4 mr-2" />
          Clear
        </Button>
      )}
    </div>
  );

  return (
    <div className="animate-slide-up">
      {/* Page Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 mb-8">
        <div>
          <h1 className="page-title">Invoices</h1>
          <p className="page-subtitle">Track and manage vendor invoices and compliance status.</p>
        </div>
        <div className="flex">
          <Button
            className="rounded-r-none"
            onClick={() => setNewInvoiceDialogOpen(true)}
            disabled={!canCreate}
          >
            <Plus className="w-5 h-5 mr-2" />
            New Invoice
          </Button>
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button className="rounded-l-none border-l border-l-primary-foreground/20 px-2" disabled={!canUpload}>
                <ChevronDown className="w-4 h-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem disabled={!canUpload} onClick={() => navigate('/tools/extract-invoice')}>
                <Upload className="w-4 h-4 mr-2" />
                Upload Invoice Package
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </div>

      {/* Invoices Table with AG Grid */}
      <AGDataGrid<Invoice>
        rowData={invoices}
        columnDefs={columnDefs}
        totalCount={totalCount}
        pageIndex={pageIndex}
        pageSize={pageSize}
        pageSizeOptions={pageSizeOptions}
        searchValue={searchValue}
        searchPlaceholder="Search invoices..."
        isLoading={isLoading && invoices.length === 0}
        isFetching={isLoading}
        onSearchChange={setSearchValue}
        onPageChange={setPageIndex}
        onPageSizeChange={setPageSize}
        onRowClicked={handleRowClick}
        toolbarActions={toolbarActions}
        height="calc(100vh - 320px)"
      />

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Invoice</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete invoice "{invoiceToDelete?.invoice_number || invoiceToDelete?.id?.slice(0, 8)}"? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <NewInvoiceDialog
        open={newInvoiceDialogOpen}
        onOpenChange={setNewInvoiceDialogOpen}
        onSave={(invoiceId) => navigate(`/invoices/${invoiceId}/edit`)}
      />
    </div>
  );
}
